import React, { memo, useEffect, useState } from 'react';
import {
  Box,
  ButtonSelect,
  Icon,
  InlineMessage,
  InputField,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';

import { MIN_COMMENT_LENGTH } from '../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../shared/hooks';

const REMEDIATE_OPTIONS = [
  {
    label: 'Create Request',
    value: 'CREATE_REQUEST',
    isChecked: true,
  },
  {
    label: 'Create Task',
    value: 'CREATE_TASK',
    isChecked: false,
  },
];

interface RemediationCommentScreenProps {
  isCommentCritical: boolean;
  setIsCommentCritical: (state: boolean) => void;
  commentValue: string;
  setCommentValue: (text: string) => void;
  setRemediateValue: (text: string) => void;
}

/**
 * RemediationImpactScreen
 */
function RemediationCommentScreen({
  isCommentCritical,
  setIsCommentCritical,
  commentValue,
  setCommentValue,
  setRemediateValue,
}: RemediationCommentScreenProps) {
  const l = useInvertedLocalize();

  const [remediateOptions, setRemediateOptions] =
    useState<MenuOption[]>(REMEDIATE_OPTIONS);

  useEffect(() => {
    const activeOption = remediateOptions.find((r) => r.isChecked);

    if (activeOption) {
      setRemediateValue(activeOption.value);
    } else {
      setRemediateValue('CREATE_REQUEST');
    }
  }, [remediateOptions, setRemediateValue]);

  const handleChange = (selectedOption: MenuOption) => {
    setRemediateOptions((prev) =>
      prev.map((r) =>
        r.value === selectedOption.value
          ? { ...r, isChecked: true }
          : { ...r, isChecked: false }
      )
    );
  };

  return (
    <Box tag='div' className='RemediationCommentScreen'>
      <div className='RemediationCommentScreen-remediateWrapper'>
        <Typography kind='label'>{l('Remediate Option')}</Typography>
        <Menu
          options={remediateOptions}
          onChange={handleChange}
          trigger={
            <ButtonSelect
              kind='rectangle'
              label={remediateOptions.find((o) => o.isChecked)?.label}
              size='small'
            />
          }
        />
      </div>
      <InputField
        name='move-to-open-action-comment'
        kind='multiline'
        label={
          <Typography kind='label' htmlFor='move-to-open-action-comment'>
            {l('Comments')}
          </Typography>
        }
        tabIndex={0}
        value={commentValue}
        setValue={setCommentValue}
        placeholder={l('Provide a reason for your decision')}
        isRequired={
          commentValue.trim().length < MIN_COMMENT_LENGTH || isCommentCritical
        }
        isCritical={isCommentCritical}
        setIsCritical={setIsCommentCritical}
        CriticalHelperText={
          <InlineMessage
            text={
              commentValue.trim().length < 1
                ? l('This required field is incomplete')
                : `Your comments must contain more than ${
                    MIN_COMMENT_LENGTH - 1
                  } characters.`
            }
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        }
      />
    </Box>
  );
}

export default memo(RemediationCommentScreen);
